/**
 * Organization JotBill Fees Section
 *
 * Admin view of an organization's JotBill fee invoices (jot-take PRD /
 * GDN-1737) plus the manual billing run flow: pick a period, preview the
 * dry run (exact would-be invoice lines), then explicitly commit. Commit
 * creates a real draft fee invoice that will be charged to the org, so
 * the confirm step spells out the amount.
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as RunIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import type { JotBillFeeRunResponse } from '@store/generated/billing';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import {
  useJotBillFeeInvoiceDetailQuery,
  useJotBillFeeInvoicesQuery,
  useRunJotBillFeeBillingMutation,
} from '../store/hooks';

const formatCents = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString() : '—';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'primary' | 'success' | 'error' | 'warning'> = {
  draft: 'default',
  finalized: 'info',
  invoiced: 'primary',
  paid: 'success',
  payment_failed: 'error',
  void: 'warning',
};

const SKIP_MESSAGES: Record<string, string> = {
  not_enabled: 'JotBill take billing is not enabled for this organization.',
  no_candidates: 'No unbilled insurance payments in this period.',
  below_minimum: 'Total is below the $1.00 minimum — lines roll into the next run.',
  negative_total: 'Credits exceed fees — the balance carries forward.',
};

function InvoiceRow({
  organizationId,
  invoice,
}: {
  organizationId: string;
  invoice: {
    jotbill_fee_invoice_id: string;
    period_start: string;
    period_end: string;
    take_rate_bps: number;
    fee_subtotal_cents: number;
    credit_cents: number;
    total_cents: number;
    status: string;
    stripe_invoice_id?: string | null;
    run_source: string;
    created_at?: string | null;
  };
}) {
  const [open, setOpen] = useState(false);
  const { data: detail, isLoading } = useJotBillFeeInvoiceDetailQuery(
    { organizationId, invoiceId: invoice.jotbill_fee_invoice_id },
    { skip: !open }
  );

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)} aria-label="Toggle invoice lines">
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}
        </TableCell>
        <TableCell>{(invoice.take_rate_bps / 100).toFixed(0)}%</TableCell>
        <TableCell align="right">{formatCents(invoice.fee_subtotal_cents)}</TableCell>
        <TableCell align="right">
          {invoice.credit_cents > 0 ? `-${formatCents(invoice.credit_cents)}` : '—'}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {formatCents(invoice.total_cents)}
        </TableCell>
        <TableCell>
          <Chip
            label={invoice.status}
            size="small"
            color={STATUS_COLORS[invoice.status] ?? 'default'}
          />
        </TableCell>
        <TableCell>
          {invoice.stripe_invoice_id ? (
            <Button
              size="small"
              endIcon={<OpenInNewIcon fontSize="inherit" />}
              component="a"
              href={`https://dashboard.stripe.com/invoices/${invoice.stripe_invoice_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe
            </Button>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell>{invoice.run_source}</TableCell>
        <TableCell>{formatDate(invoice.created_at)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={10} sx={{ py: 0, border: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 2 }}>
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Claim</TableCell>
                      <TableCell>Remittance claim</TableCell>
                      <TableCell align="right">Insurance paid</TableCell>
                      <TableCell align="right">Fee</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail?.lines ?? []).map(line => (
                      <TableRow key={line.remittance_claim_id}>
                        <TableCell>
                          {line.line_type === 'reversal_credit' ? (
                            <Chip label="credit" size="small" color="warning" />
                          ) : (
                            'fee'
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {line.claim_id ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {line.remittance_claim_id}
                        </TableCell>
                        <TableCell align="right">{formatCents(line.base_payment_cents)}</TableCell>
                        <TableCell align="right">{formatCents(line.fee_cents)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function OrganizationJotBillFeesSection({ organizationId }: { organizationId: string }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, refetch } = useJotBillFeeInvoicesQuery({ organizationId });
  const [runBilling, { isLoading: isRunning }] = useRunJotBillFeeBillingMutation();

  // Backfill dialog state: 'closed' -> 'period' -> 'preview' (after dry run)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [preview, setPreview] = useState<JotBillFeeRunResponse | null>(null);

  const closeDialog = () => {
    setDialogOpen(false);
    setPreview(null);
  };

  const runRequest = (commit: boolean) =>
    runBilling({
      organizationId,
      runJotBillFeeBillingRequest: {
        period_start: `${periodStart}T00:00:00+00:00`,
        period_end: `${periodEnd}T00:00:00+00:00`,
        commit,
      },
    }).unwrap();

  const handlePreview = async () => {
    try {
      setPreview(await runRequest(false));
    } catch (error) {
      enqueueSnackbar(extractApiErrorMessage(error, 'Dry run failed'), { variant: 'error' });
    }
  };

  const handleCommit = async () => {
    try {
      const result = await runRequest(true);
      if (result.invoice_id) {
        enqueueSnackbar(`Created fee invoice for ${formatCents(result.total_cents)}`, {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(
          SKIP_MESSAGES[result.skipped_reason ?? ''] ?? 'No invoice was created',
          { variant: 'info' }
        );
      }
      closeDialog();
      refetch();
    } catch (error) {
      enqueueSnackbar(extractApiErrorMessage(error, 'Billing run failed'), { variant: 'error' });
    }
  };

  const invoices = data?.invoices ?? [];

  return (
    <Card
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: 2 }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              JotBill Fee Invoices
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Weekly participation fees on insurance collections. Manual runs preview before
              committing.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RunIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Run manual billing
          </Button>
        </Box>

        {isLoading ? (
          <CircularProgress size={24} />
        ) : invoices.length === 0 ? (
          <Alert severity="info">No fee invoices yet for this organization.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Period</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell align="right">Fees</TableCell>
                <TableCell align="right">Credits</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Stripe</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map(invoice => (
                <InvoiceRow
                  key={invoice.jotbill_fee_invoice_id}
                  organizationId={organizationId}
                  invoice={invoice}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Manual JotBill billing run</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bills unbilled insurance payments ingested in the period below. Already-billed
            payments are excluded automatically and can never be double-billed. Manual runs use
            this exact period — they ignore the org's effective-from date, which is what makes
            backfills possible.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              type="date"
              label="Period start"
              value={periodStart}
              onChange={e => {
                setPeriodStart(e.target.value);
                setPreview(null);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              size="small"
              type="date"
              label="Period end (exclusive)"
              value={periodEnd}
              onChange={e => {
                setPeriodEnd(e.target.value);
                setPreview(null);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Button
              variant="outlined"
              onClick={handlePreview}
              disabled={!periodStart || !periodEnd || isRunning}
              startIcon={isRunning && !preview ? <CircularProgress size={16} /> : undefined}
            >
              Preview (dry run)
            </Button>
          </Box>

          {preview && preview.skipped_reason && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {SKIP_MESSAGES[preview.skipped_reason] ?? preview.skipped_reason}
              {preview.total_cents !== 0 && ` (computed total: ${formatCents(preview.total_cents)})`}
            </Alert>
          )}

          {preview && !preview.skipped_reason && (
            <>
              <Alert severity="warning" sx={{ mb: 1 }}>
                Committing will create a fee invoice for{' '}
                <strong>{formatCents(preview.total_cents)}</strong> ({preview.line_count} lines:{' '}
                {formatCents(preview.fee_subtotal_cents)} fees
                {preview.credit_cents > 0 && ` − ${formatCents(preview.credit_cents)} credits`}, at{' '}
                {(preview.take_rate_bps / 100).toFixed(0)}%) that will be charged to this
                organization.
              </Alert>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Claim</TableCell>
                    <TableCell align="right">Insurance paid</TableCell>
                    <TableCell align="right">Fee</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(preview.lines ?? []).map(line => (
                    <TableRow key={line.remittance_claim_id}>
                      <TableCell>
                        {line.line_type === 'reversal_credit' ? 'credit' : 'fee'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {line.claim_id ?? '—'}
                      </TableCell>
                      <TableCell align="right">{formatCents(line.base_payment_cents)}</TableCell>
                      <TableCell align="right">{formatCents(line.fee_cents)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isRunning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCommit}
            disabled={!preview || !!preview.skipped_reason || isRunning}
            startIcon={isRunning ? <CircularProgress size={16} /> : undefined}
          >
            Commit & create invoice
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
